import type { InputHTMLAttributes, ReactNode } from 'react';

interface CheckboxFieldProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'type'
> {
  id: string;
  /**
   * The label content. A node rather than a string because these labels carry
   * links to the documents being agreed to.
   */
  label: ReactNode;
  /** Error copy; also wires aria-invalid + aria-describedby. */
  error?: string | null;
}

/**
 * A real `<input type="checkbox">`, styled rather than replaced.
 *
 * The usual trick — a visually-hidden input under a styled `<span>` — loses
 * the focus outline, the 44px target and the forced-colors rendering all at
 * once, and this is a mandatory-access surface: someone who cannot operate it
 * cannot sign in at all (docs/auth/35-design/accessibility.md). `accent-color`
 * gets the brand onto the control without taking it apart.
 */
export function CheckboxField({
  id,
  label,
  error,
  ...rest
}: CheckboxFieldProps) {
  const errorId = `${id}-error`;

  return (
    <div>
      {/* min-h-11 is the 44px target: the whole row is the hit area, not
          just the 20px box (docs/auth/35-design/components.md). */}
      <label
        htmlFor={id}
        className="flex min-h-11 items-start gap-3 py-1 text-sm leading-relaxed text-buds-dark"
      >
        <input
          {...rest}
          type="checkbox"
          id={id}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? errorId : undefined}
          className={[
            'mt-0.5 h-5 w-5 shrink-0 rounded border bg-buds-surface',
            'accent-buds-green focus-ring',
            error ? 'border-buds-danger' : 'border-buds-muted/80',
          ].join(' ')}
        />
        <span>{label}</span>
      </label>
      {error ? (
        <p id={errorId} className="mt-1 text-sm text-buds-danger">
          {error}
        </p>
      ) : null}
    </div>
  );
}
